// Login.js
// Mirrors LoginView.swift + SignUpView.swift exactly.
//
// Flow:
//   Login screen → LOG IN button → home (if success)
//                → CREATE ACCOUNT → sign up modal
//                → FORGOT PASSWORD → reset modal
//                → CONTINUE AS GUEST → guest warning overlay → home
//
// Auth state lives in AuthContext — no Firebase calls yet (stubs in place).

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../managers/AuthContext";
import icon from "../assets/icon.png";
import wordmark from "../assets/Rosterable.png";

const APP_THEME = "#d4f53c";

export default function Login() {
  const navigate = useNavigate();
  const auth = useAuth();

  const [view, setView] = useState("login"); // "login" | "signup" | "forgot"
  const [showGuestWarning, setShowGuestWarning] = useState(false);

  // Navigate home only once a REAL signed-in user exists.
  //
  // We deliberately do NOT navigate the moment auth.login()/signUp() resolves.
  // Those resolve as soon as Firebase signs in, but the app's `user` state is
  // set a tick later by the onAuthStateChanged listener in AuthContext.
  // Navigating early would land while `user` is still null — that was the
  // "first attempt does nothing" bug. Watching `auth.user` keeps navigation in
  // lockstep with the real auth state.
  //
  // IMPORTANT: this keys off auth.user, NOT auth.isLoggedIn. isLoggedIn is also
  // true for guests, so redirecting on it bounced a guest straight back home the
  // instant they opened /login — making it impossible to upgrade to a real
  // account. Guests must be able to stay on the login screen. The
  // continue-as-guest path navigates home explicitly in its own handler below.
  useEffect(() => {
    if (auth.user) {
      navigate("/", { replace: true });
    }
  }, [auth.user, navigate]);

  return (
    <div style={styles.page}>
      {/* Ambient top glow */}
      <div style={styles.glow} />

      {view === "login" && (
        <LoginForm
          onSignUp={() => setView("signup")}
          onForgot={() => setView("forgot")}
          onGuest={() => setShowGuestWarning(true)}
          auth={auth}
        />
      )}

      {view === "signup" && (
        <SignUpForm
          onBack={() => setView("login")}
          auth={auth}
        />
      )}

      {view === "forgot" && (
        <ForgotPasswordForm
          onBack={() => setView("login")}
          auth={auth}
        />
      )}

      {/* Guest warning overlay */}
      {showGuestWarning && (
        <GuestWarning
          onCreateAccount={() => { setShowGuestWarning(false); setView("signup"); }}
          onContinueAsGuest={() => {
            auth.continueAsGuest();
            setShowGuestWarning(false);
            // The redirect effect above only fires for real users, so the
            // explicit guest path navigates home itself.
            navigate("/", { replace: true });
          }}
          onClose={() => setShowGuestWarning(false)}
        />
      )}
    </div>
  );
}

// ─── Login Form ───────────────────────────────────────────────────────────────
// Mirrors LoginView.swift body

function LoginForm({ onSignUp, onForgot, onGuest, auth }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [focusedField, setFocusedField] = useState(null);

  const isFormReady = username.trim().length > 0 && password.length > 0;

  async function handleLogin() {
    setError(null);
    if (!username.trim()) { setError("Please enter your username."); return; }
    if (!password) { setError("Please enter your password."); return; }
    try {
      await auth.login(username.trim(), password);
      // Navigation happens in the parent's isLoggedIn effect once auth state is ready.
    } catch (err) {
      setError(err.message || "Incorrect username or password. Please try again.");
    }
  }

  return (
    <div style={styles.formContainer}>
      <div style={styles.scrollContent}>
        {/* Branding */}
        <div style={styles.brandingSection}>
          <img src={icon} alt="Rosterable" style={styles.logoIcon} />
          <img src={wordmark} alt="Rosterable" style={styles.wordmarkLarge} />
          <div style={styles.brandingRule} />
        </div>

        {/* Form */}
        <div style={styles.form}>
          <AuthTextField
            label="USERNAME"
            placeholder="Enter username"
            value={username}
            onChange={setUsername}
            field="username"
            focusedField={focusedField}
            onFocus={() => setFocusedField("username")}
            onBlur={() => setFocusedField(null)}
            autoCapitalize="none"
          />
          <AuthTextField
            label="PASSWORD"
            placeholder="Enter password"
            value={password}
            onChange={setPassword}
            field="password"
            focusedField={focusedField}
            onFocus={() => setFocusedField("password")}
            onBlur={() => setFocusedField(null)}
            isSecure
          />

          {error && <ErrorMessage message={error} />}

          <PrimaryButton
            label="LOG IN"
            isLoading={auth.isLoading}
            isEnabled={isFormReady && !auth.isLoading}
            onClick={handleLogin}
          />
        </div>

        {/* Forgot password */}
        <button style={styles.subtleLink} onClick={onForgot}>
          FORGOT PASSWORD?
        </button>

        <div style={styles.divider} />

        {/* Sign up */}
        <button
          style={{ ...styles.outlineBtn }}
          onClick={onSignUp}
        >
          CREATE ACCOUNT
        </button>

        {/* Guest */}
        <button style={{ ...styles.outlineBtn }} onClick={onGuest}>
          CONTINUE AS GUEST
        </button>
      </div>
    </div>
  );
}

// ─── Sign Up Form ─────────────────────────────────────────────────────────────
// Mirrors SignUpView.swift

function SignUpForm({ onBack, auth }) {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [confirmAge, setConfirmAge] = useState(false);
  const [error, setError] = useState(null);
  const [focusedField, setFocusedField] = useState(null);

  const isFormReady = username.trim() && email.trim() && password && confirmPassword && agreeToTerms && confirmAge;

  async function handleSignUp() {
    setError(null);
    if (!username.trim()) { setError("Please enter a username."); return; }
    if (username.trim().length < 3) { setError("Username must be at least 3 characters."); return; }
    if (!email.includes("@")) { setError("Please enter a valid email address."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirmPassword) { setError("Passwords do not match."); return; }
    try {
      await auth.signUp(username.trim(), email.trim(), password);
      // Navigation happens in the parent's isLoggedIn effect once auth state is ready.
    } catch (err) {
      setError(err.message || "Sign up failed. Please try again.");
    }
  }

  return (
    <div style={styles.formContainer}>
      <div style={styles.scrollContent}>
        {/* Header */}
        <div style={styles.sheetHeader}>
          <button style={styles.backBtn} onClick={onBack}>‹</button>
          <span style={styles.sheetTitle}>CREATE ACCOUNT</span>
          <div style={{ width: 36 }} />
        </div>

        {/* Logo */}
        <div style={{ ...styles.brandingSection, paddingTop: 8 }}>
          <img src={icon} alt="Rosterable" style={{ ...styles.logoIcon, width: 56, height: 56 }} />
          <div style={styles.brandingRule} />
        </div>

        {/* Form */}
        <div style={styles.form}>
          <AuthTextField label="USERNAME" placeholder="Enter username" value={username} onChange={setUsername}
            field="username" focusedField={focusedField} onFocus={() => setFocusedField("username")} onBlur={() => setFocusedField(null)} autoCapitalize="none" />
          <AuthTextField label="EMAIL" placeholder="Enter email address" value={email} onChange={setEmail}
            field="email" focusedField={focusedField} onFocus={() => setFocusedField("email")} onBlur={() => setFocusedField(null)} type="email" autoCapitalize="none" />
          <AuthTextField label="PASSWORD" placeholder="Create a password" value={password} onChange={setPassword}
            field="password" focusedField={focusedField} onFocus={() => setFocusedField("password")} onBlur={() => setFocusedField(null)} isSecure />
          <AuthTextField label="CONFIRM PASSWORD" placeholder="Confirm password" value={confirmPassword} onChange={setConfirmPassword}
            field="confirmPassword" focusedField={focusedField} onFocus={() => setFocusedField("confirmPassword")} onBlur={() => setFocusedField(null)} isSecure />

          {/* Terms checkbox */}
          <div style={styles.checkboxRow}>
            <Checkbox checked={agreeToTerms} onChange={setAgreeToTerms} />
            <div style={styles.checkboxText}>
              <span style={styles.checkboxLabel}>I agree to the </span>
              <a href="/terms-of-service" target="_blank" style={styles.checkboxLink}>Terms of Service</a>
              <span style={styles.checkboxLabel}> and </span>
              <a href="/privacy-policy" target="_blank" style={styles.checkboxLink}>Privacy Policy</a>
            </div>
          </div>

          {/* Age checkbox */}
          <div style={styles.checkboxRow}>
            <Checkbox checked={confirmAge} onChange={setConfirmAge} />
            <span style={styles.checkboxLabel}>I confirm that I am 13 years of age or older.</span>
          </div>

          {error && <ErrorMessage message={error} />}

          <PrimaryButton
            label="CREATE ACCOUNT"
            isLoading={auth.isLoading}
            isEnabled={!!isFormReady && !auth.isLoading}
            onClick={handleSignUp}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Forgot Password Form ─────────────────────────────────────────────────────
// Mirrors forgotPasswordSheet in LoginView.swift

function ForgotPasswordForm({ onBack, auth }) {
  const [mode, setMode] = useState("username"); // "username" | "email"
  const [usernameVal, setUsernameVal] = useState("");
  const [emailVal, setEmailVal] = useState("");
  const [message, setMessage] = useState(null);
  const [isSending, setIsSending] = useState(false);

  const inputEmpty = mode === "username" ? !usernameVal.trim() : !emailVal.trim();

  async function handleReset() {
    setMessage(null);
    setIsSending(true);
    try {
      const result = await auth.sendPasswordReset(mode, mode === "username" ? usernameVal : emailVal);
      setMessage(result);
    } catch {
      setMessage("Something went wrong. Please try again.");
    } finally {
      setIsSending(false);
    }
  }

  const isSuccess = message?.startsWith("✅");

  return (
    <div style={styles.formContainer}>
      <div style={styles.scrollContent}>
        {/* Header */}
        <div style={styles.sheetHeader}>
          <button style={styles.backBtn} onClick={onBack}>‹</button>
          <span style={styles.sheetTitle}>RESET PASSWORD</span>
          <div style={{ width: 36 }} />
        </div>

        {/* Icon + description */}
        <div style={styles.resetIconSection}>
          <span style={{ fontSize: 36, color: APP_THEME }}>🔒</span>
          <p style={styles.resetDescription}>
            Enter your username or email and we'll send a reset link.
          </p>
        </div>

        {/* Mode toggle */}
        <div style={styles.modeToggle}>
          {["username", "email"].map(m => (
            <button
              key={m}
              style={{
                ...styles.modeBtn,
                background: mode === m ? APP_THEME : "transparent",
                color: mode === m ? "#000" : "rgba(255,255,255,0.45)",
              }}
              onClick={() => { setMode(m); setMessage(null); }}
            >
              {m.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Input */}
        <div style={styles.form}>
          <AuthTextField
            label={mode === "username" ? "USERNAME" : "EMAIL"}
            placeholder={mode === "username" ? "Enter your username" : "Enter your email"}
            value={mode === "username" ? usernameVal : emailVal}
            onChange={mode === "username" ? setUsernameVal : setEmailVal}
            field="reset"
            focusedField={null}
            onFocus={() => {}}
            onBlur={() => {}}
            type={mode === "email" ? "email" : "text"}
            autoCapitalize="none"
          />

          {/* Message */}
          <div style={{ minHeight: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {message && (
              <p style={{ ...styles.resetMessage, color: isSuccess ? "#4caf7d" : "#f25c5c" }}>
                {message}
              </p>
            )}
          </div>

          {/* Send button */}
          <PrimaryButton
            label={isSending ? "SENDING..." : "SEND RESET LINK"}
            isLoading={isSending}
            isEnabled={!inputEmpty && !isSending}
            onClick={handleReset}
            dimWhenDisabled
          />
        </div>
      </div>
    </div>
  );
}

// ─── Guest Warning Overlay ────────────────────────────────────────────────────
// Mirrors guestWarningOverlay in LoginView.swift

function GuestWarning({ onCreateAccount, onContinueAsGuest, onClose }) {
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.warningCard} onClick={e => e.stopPropagation()}>
        {/* Icon */}
        <div style={styles.warningIcon}>
          <span style={{ fontSize: 28, color: APP_THEME }}>👤?</span>
        </div>

        <p style={styles.warningTitle}>Guest Scores Won't Be Saved</p>
        <p style={styles.warningBody}>
          Create a free account to save your scores, compete on the global leaderboard, and unlock Dynasty mode.
        </p>

        <button
          style={styles.primaryBtn}
          onClick={onCreateAccount}
        >
          CREATE AN ACCOUNT
        </button>

        <button
          style={styles.ghostBtn}
          onClick={onContinueAsGuest}
        >
          CONTINUE AS GUEST
        </button>
      </div>
    </div>
  );
}

// ─── Shared Sub-components ────────────────────────────────────────────────────

function AuthTextField({ label, placeholder, value, onChange, field, focusedField, onFocus, onBlur, isSecure, type = "text", autoCapitalize }) {
  const isFocused = focusedField === field;
  return (
    <div style={styles.fieldWrapper}>
      <span style={styles.fieldLabel}>{label}</span>
      <input
        type={isSecure ? "password" : type}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        autoCapitalize={autoCapitalize}
        autoCorrect="off"
        autoComplete={isSecure ? "current-password" : type === "email" ? "email" : "username"}
        style={{
          ...styles.fieldInput,
          borderColor: isFocused ? `${APP_THEME}99` : "rgba(255,255,255,0.12)",
          borderWidth: isFocused ? "1.5px" : "1px",
          outline: "none",
        }}
      />
    </div>
  );
}

function PrimaryButton({ label, isLoading, isEnabled, onClick, dimWhenDisabled }) {
  return (
    <button
      style={{
        ...styles.primaryBtn,
        opacity: isEnabled ? 1 : (dimWhenDisabled ? 0.4 : 0.5),
        boxShadow: isEnabled ? `0 6px 20px ${APP_THEME}59` : "none",
        transition: "all 0.2s ease",
      }}
      onClick={onClick}
      disabled={!isEnabled}
    >
      {isLoading
        ? <span style={styles.spinner}>●</span>
        : <span>{label}</span>
      }
    </button>
  );
}

function Checkbox({ checked, onChange }) {
  return (
    <button
      style={{
        ...styles.checkbox,
        background: checked ? APP_THEME : "transparent",
        borderColor: APP_THEME,
      }}
      onClick={() => onChange(!checked)}
    >
      {checked && <span style={{ fontSize: 11, fontWeight: 800, color: "#000" }}>✓</span>}
    </button>
  );
}

function ErrorMessage({ message }) {
  return (
    <p style={styles.errorText}>{message}</p>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight: "100vh",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  glow: {
    position: "fixed",
    top: 0, left: 0, right: 0,
    height: "40vh",
    background: `radial-gradient(ellipse at top left, ${APP_THEME}12, transparent 60%)`,
    pointerEvents: "none",
    zIndex: 0,
  },
  formContainer: {
    width: "100%",
    maxWidth: "480px",
    padding: "0 30px",
    position: "relative",
    zIndex: 1,
  },
  scrollContent: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    paddingTop: "48px",
    paddingBottom: "48px",
  },

  // Branding
  brandingSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "14px",
  },
  logoIcon: {
    width: "72px",
    height: "72px",
    objectFit: "contain",
  },
  wordmarkLarge: {
    height: "39px",
    width: "auto",
  },
  brandingRule: {
    height: "1px",
    width: "60%",
    background: `${APP_THEME}66`,
    marginTop: "4px",
  },

  // Sheet header (signup / forgot)
  sheetHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  backBtn: {
    background: "none",
    border: "none",
    color: "rgba(255,255,255,0.6)",
    fontSize: "26px",
    fontWeight: "300",
    cursor: "pointer",
    width: "36px",
    lineHeight: 1,
    padding: 0,
  },
  sheetTitle: {
    fontSize: "17px",
    fontWeight: "800",
    color: "#fff",
    letterSpacing: "1px",
  },

  // Form
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  fieldWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  fieldLabel: {
    fontSize: "11px",
    fontWeight: "700",
    color: "rgba(255,255,255,0.4)",
    letterSpacing: "1.5px",
  },
  fieldInput: {
    background: "rgba(255,255,255,0.07)",
    border: "1px solid",
    borderRadius: "10px",
    padding: "14px 16px",
    fontSize: "16px",
    fontWeight: "500",
    color: "#ffffff",
    width: "100%",
    boxSizing: "border-box",
    fontFamily: "inherit",
    transition: "border-color 0.15s ease",
  },

  // Buttons
  primaryBtn: {
    width: "100%",
    height: "52px",
    borderRadius: "12px",
    background: APP_THEME,
    border: "none",
    fontSize: "15px",
    fontWeight: "800",
    color: "#000000",
    letterSpacing: "1px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  outlineBtn: {
    width: "100%",
    height: "52px",
    borderRadius: "12px",
    background: "rgba(255,255,255,0.04)",
    border: `1.5px solid ${APP_THEME}4d`,
    fontSize: "15px",
    fontWeight: "800",
    color: APP_THEME,
    letterSpacing: "1px",
    cursor: "pointer",
  },
  ghostBtn: {
    width: "100%",
    height: "44px",
    borderRadius: "12px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    fontSize: "14px",
    fontWeight: "600",
    color: "rgba(255,255,255,0.45)",
    letterSpacing: "0.5px",
    cursor: "pointer",
  },
  subtleLink: {
    background: "none",
    border: "none",
    fontSize: "11px",
    fontWeight: "700",
    color: "rgba(255,255,255,0.4)",
    letterSpacing: "1.5px",
    cursor: "pointer",
    textAlign: "center",
  },
  divider: {
    height: "1px",
    background: "rgba(255,255,255,0.08)",
  },

  // Checkboxes
  checkboxRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
  },
  checkbox: {
    width: "20px",
    height: "20px",
    borderRadius: "4px",
    border: `2px solid ${APP_THEME}`,
    flexShrink: 0,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.15s ease",
  },
  checkboxText: {
    fontSize: "12px",
    fontWeight: "500",
    color: "rgba(255,255,255,0.6)",
    lineHeight: 1.5,
    flexWrap: "wrap",
  },
  checkboxLabel: {
    fontSize: "12px",
    fontWeight: "500",
    color: "rgba(255,255,255,0.6)",
  },
  checkboxLink: {
    fontSize: "12px",
    fontWeight: "600",
    color: APP_THEME,
    textDecoration: "none",
  },

  // Errors
  errorText: {
    fontSize: "13px",
    fontWeight: "500",
    color: "#f25c5c",
    textAlign: "center",
    margin: 0,
  },

  // Forgot password
  resetIconSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    textAlign: "center",
  },
  resetDescription: {
    fontSize: "14px",
    color: "rgba(255,255,255,0.5)",
    lineHeight: 1.6,
    margin: 0,
    maxWidth: "280px",
  },
  modeToggle: {
    display: "flex",
    background: "rgba(255,255,255,0.06)",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.1)",
    overflow: "hidden",
    padding: "3px",
    gap: "2px",
  },
  modeBtn: {
    flex: 1,
    height: "36px",
    border: "none",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: "800",
    letterSpacing: "1px",
    cursor: "pointer",
    transition: "all 0.18s ease",
  },
  resetMessage: {
    fontSize: "13px",
    fontWeight: "500",
    textAlign: "center",
    margin: 0,
  },
  spinner: {
    animation: "spin 1s linear infinite",
    display: "inline-block",
    fontSize: "16px",
  },

  // Guest warning
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.75)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
    padding: "0 30px",
  },
  warningCard: {
    background: "rgb(15,15,26)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "20px",
    padding: "28px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    maxWidth: "420px",
    width: "100%",
    boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
  },
  warningIcon: {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    background: `${APP_THEME}1f`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "6px",
  },
  warningTitle: {
    fontSize: "19px",
    fontWeight: "800",
    color: "#fff",
    textAlign: "center",
    margin: 0,
  },
  warningBody: {
    fontSize: "14px",
    color: "rgba(255,255,255,0.55)",
    textAlign: "center",
    lineHeight: 1.6,
    margin: "0 0 16px",
  },
};
